import { createSelector, createSlice } from '@reduxjs/toolkit';
import { fetchTasks } from './operatorTasksThunks';
import { parseServerTime, taskPath } from './operatorTasksUtils';
import { OPERATOR_ERROR } from '../operator/operatorErrors';
import { operatorWarehouseSelected } from '../operator/operatorSlice';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';
import { isBackgroundRequest } from '../feedback/feedbackSlice';

/*
 * The tray of the warehouse on Inicio, and the local clock its times are
 * read with. The tray is asked when Inicio opens, with «Actualizar», when the
 * operator finishes a task (see `operatorTasksListeners`) and, while Inicio
 * sits still, on the clock's tick (see `operatorRefresh`): that one in the
 * background, so the screen shows no loading.
 */
const initialState = {
  /** `GET tasks/` as it came: `{generated_at, next, mine, open, open_total, counts, queues}`. */
  data: null,
  /** The warehouse `data` is of. */
  warehouseId: null,
  loading: false,
  error: null,
  /** Only the last request's answer lands. */
  requestId: null,
  /** When the tray arrived, by the phone's clock. */
  receivedAt: null,
  /** `generated_at` minus `receivedAt`: how far the phone's clock is behind the server's. */
  clockOffsetMs: 0,
  /** The phone's time, as the clock last ticked; `null` until it starts. */
  now: null,
};

const operatorTasksSlice = createSlice({
  name: 'operatorTasks',
  initialState,
  reducers: {
    /** The layout opened: a listener starts ticking. */
    operatorClockStarted: () => {},
    /** The layout closed: the listener stops. */
    operatorClockStopped: () => {},
    /** @param {{payload: number}} action - The phone's time, read by the listener. */
    operatorClockTicked: (state, action) => {
      state.now = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // The clock is the phone's; everything else was about the other warehouse.
      .addCase(operatorWarehouseSelected, (state) => ({ ...initialState, now: state.now }))

      .addCase(fetchTasks.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        if (isBackgroundRequest(action)) return;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        const { receivedAt, warehouseId } = action.meta;
        const generatedAt = parseServerTime(action.payload?.generated_at);
        state.loading = false;
        state.error = null;
        state.data = action.payload;
        state.warehouseId = warehouseId;
        state.receivedAt = receivedAt;
        state.now = receivedAt;
        state.clockOffsetMs = generatedAt === null ? 0 : generatedAt - receivedAt;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        // A refresh in the background fails in silence: the tray stays as it was, and the next one tries again.
        if (action.meta.condition || action.meta.requestId !== state.requestId || isBackgroundRequest(action)) return;
        state.loading = false;
        state.error = action.payload ?? { kind: OPERATOR_ERROR.REFUSED, message: action.error.message };
      });
  },
});

export const { operatorClockStarted, operatorClockStopped, operatorClockTicked } = operatorTasksSlice.actions;

/** The phone's time, as the clock last ticked. */
export const selectTasksClock = (state) => state.operatorTasks.now;

/** The server's time, as the phone's clock corrected by the last tray; `null` before the clock starts. */
export const selectTasksNow = (state) => {
  const { now, clockOffsetMs } = state.operatorTasks;
  return now == null ? null : now + clockOffsetMs;
};

/** Whether the tray of the warehouse in use came at least once. */
export const selectTasksLoadedHere = (state) => {
  const { receivedAt, warehouseId } = state.operatorTasks;
  return receivedAt != null && warehouseId === selectCurrentOperatorWarehouseId(state);
};

/** The tray's `queues` for the warehouse in use, or `null`: the flow cards then count with `me`. */
export const selectTaskQueues = (state) =>
  selectTasksLoadedHere(state) ? state.operatorTasks.data?.queues ?? null : null;

/**
 * How many tasks the tray holds: what the operator has on and everything
 * left to do, shown or not. `null` until the tray of this warehouse comes,
 * while it is asked again and once it failed: a number then could be stale.
 */
export const selectTrayTotal = (state) => {
  const { data, loading, error } = state.operatorTasks;
  if (!data || loading || error || !selectTasksLoadedHere(state)) return null;
  return (data.mine || []).length + (data.open_total ?? (data.open || []).length);
};

const withPositions = (tasks, first) => tasks.map((task, index) => ({ task, position: first + index }));

/**
 * The tray as Inicio shows it, numbered in order: «Siguiente» first, then
 * the rest of what the operator has on, then what is left to do. A task
 * this front cannot open is left out. `null` until the tray of this
 * warehouse comes.
 *
 * @returns {{next: object|null, mine: Array<{task, position}>, open: Array<{task, position}>,
 *   openTotal: number, notShown: number}|null}
 */
export const selectTray = createSelector(
  [(state) => state.operatorTasks.data, selectTasksLoadedHere],
  (data, loadedHere) => {
    if (!data || !loadedHere) return null;
    const known = (task) => taskPath(task) !== null;
    const next = data.next && known(data.next) ? data.next : null;
    const rest = (tasks) => (tasks || []).filter((task) => known(task) && task.id !== next?.id);
    const mine = rest(data.mine);
    const open = rest(data.open);
    const first = next ? 2 : 1;
    return {
      next,
      mine: withPositions(mine, first),
      open: withPositions(open, first + mine.length),
      openTotal: data.open_total ?? open.length,
      notShown: Math.max((data.open_total ?? 0) - (data.open || []).length, 0),
    };
  }
);

export default operatorTasksSlice.reducer;
